import React from "react";
import styled, { keyframes } from "styled-components";
import { ListItemContainer, ListItemDivider } from "../ListItem/styles";
import {
    ProfileContainer,
    ProfileMain,
    ProfileInfo,
    NameContainer,
    Name,
    NameText,
    ActionsContainer,
    AvatarBox,
    StatusContainer,
} from "#app/components/Application/application/styles";
import {
    CommentContainer,
    CommentCaptionContainer,
} from "#app/components/Application/application/CommentSection/styles";

const shimmer = keyframes`
    0% {
        opacity: 0.6;
    }
    50% {
        opacity: 0.8;
    }
    100% {
        opacity: 0.6;
    }
`;

const SkeletonItem = styled(ListItemContainer)`
    opacity: 0.8;
    animation: ${shimmer} 1.5s ease-in-out infinite;
    pointer-events: none;

    &::before {
        content: "";
        display: block;
        width: 100%;
        height: 100%;
        background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.05) 50%,
            transparent 100%
        );
        animation: ${shimmer} 1.5s ease-in-out infinite;
    }
`;

const SkeletonAvatar = styled(AvatarBox)`
    background-color: #454650;
    animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const SkeletonNameLine = styled.div<{ $width?: string }>`
    height: 14px;
    background-color: #454650;
    border-radius: 4px;
    width: ${(props) => props.$width || "100%"};
    animation: ${shimmer} 1.5s ease-in-out infinite;
    margin-bottom: 2px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const SkeletonStatusText = styled.div`
    height: 12px;
    background-color: #454650;
    border-radius: 4px;
    width: 60px;
    animation: ${shimmer} 1.5s ease-in-out infinite;
    margin-bottom: 4px;
`;

const SkeletonStatusIcon = styled.div`
    width: 20px;
    height: 20px;
    background-color: #454650;
    border-radius: 4px;
    animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const SkeletonCommentText = styled.div`
    color: #bcbcbc;
    font-size: 13px;
    line-height: 1.4;
    opacity: 0.3;
    animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const SkeletonComment = styled(CommentCaptionContainer)`
    background-color: #333443;
    min-height: 20px;
    animation: ${shimmer} 1.5s ease-in-out infinite;
    position: relative;
    overflow: hidden;
    cursor: default;

    &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.05) 50%,
            transparent 100%
        );
        animation: ${shimmer} 1.5s ease-in-out infinite;
    }
`;

interface ListSkeletonProps {
    count?: number;
}

const ListSkeleton: React.FC<ListSkeletonProps> = ({ count = 12 }) => {
    return (
        <>
            {Array.from({ length: count }).map((_, index) => (
                <React.Fragment key={`skeleton-${index}`}>
                    <SkeletonItem>
                        <ProfileContainer>
                            <ProfileMain>
                                <SkeletonAvatar />
                                <ProfileInfo>
                                    <NameContainer>
                                        <Name>
                                            <SkeletonNameLine $width="80%" />
                                            <SkeletonNameLine $width="60%" />
                                        </Name>
                                    </NameContainer>
                                </ProfileInfo>
                            </ProfileMain>
                            <ActionsContainer>
                                <StatusContainer>
                                    <SkeletonStatusText />
                                    <div style={{ display: "flex", gap: "4px" }}>
                                        <SkeletonStatusIcon />
                                    </div>
                                </StatusContainer>
                            </ActionsContainer>
                        </ProfileContainer>
                        <CommentContainer>
                            <SkeletonComment>
                                <SkeletonCommentText>Комментарий к заявке</SkeletonCommentText>
                            </SkeletonComment>
                        </CommentContainer>
                    </SkeletonItem>
                    <ListItemDivider />
                </React.Fragment>
            ))}
        </>
    );
};

export default ListSkeleton;
